import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Minimal Web Speech API surface (not in the DOM lib typings). */
interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  0: SpeechRecognitionAlternative;
  isFinal: boolean;
  length: number;
}
interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEventLike {
  error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): SpeechRecognitionCtor | undefined {
  const scope = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition;
}

export interface SpeechRecognitionHook {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
}

/** Voice dictation via the Web Speech API; emits transcripts through `onResult`. */
export function useSpeechRecognition(
  lang: string,
  onResult: (text: string, isFinal: boolean) => void,
): SpeechRecognitionHook {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  /** Set to true only when the user explicitly calls stop(); prevents auto-restart. */
  const userStoppedRef = useRef(false);

  const supported = useMemo(() => getCtor() !== undefined, []);

  useEffect(() => {
    return () => {
      userStoppedRef.current = true;
      recognitionRef.current?.abort();
    };
  }, []);

  const start = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor) return;
    userStoppedRef.current = false;
    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      let text = '';
      let isFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (!result) continue;
        text += result[0].transcript;
        if (result.isFinal) isFinal = true;
      }
      onResultRef.current(text, isFinal);
    };
    recognition.onerror = (event) => {
      // Non-recoverable errors: stop and inform the UI.
      const nonRecoverable = ['not-allowed', 'service-not-allowed', 'audio-capture'];
      if (nonRecoverable.includes(event.error)) {
        userStoppedRef.current = true;
        setListening(false);
        return;
      }
      // Transient errors (no-speech, network, etc.): auto-restart unless user stopped.
      if (!userStoppedRef.current) {
        try {
          recognitionRef.current?.start();
        } catch {
          // If restart fails, give up gracefully.
          setListening(false);
        }
      } else {
        setListening(false);
      }
    };
    recognition.onend = () => {
      if (userStoppedRef.current) {
        setListening(false);
      } else {
        // Engine ended on its own (pause, timeout, etc.) — restart seamlessly.
        try {
          recognitionRef.current?.start();
        } catch {
          setListening(false);
        }
      }
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, [lang]);

  const stop = useCallback(() => {
    // Mark as user-initiated BEFORE calling abort so onend/onerror don't restart.
    userStoppedRef.current = true;
    recognitionRef.current?.abort();
    setListening(false);
  }, []);

  return { supported, listening, start, stop };
}
